// =============================================================================
// Campaign structure (spec §6 flow 4).
//
// A campaign template describes an ORDER, not a set of posts: teaser →
// announcement → reminder → last chance → thank you. The order is the whole
// value — five posts that each announce the same sale read as a bot, while the
// same five arranged as a build-up read as a shop running a promotion.
//
// Pure and dependency-free so expansion can be tested exhaustively without a
// database or an API key.
// =============================================================================
export const POST_ROLES = [
    "teaser",
    "announcement",
    "reminder",
    "last_chance",
    "thank_you",
    "social_proof",
    "behind_scenes",
    "tip",
    "engagement",
];
/**
 * Turns a template's steps into exactly `postCount` ordered posts.
 *
 * The client picks the number of posts, which almost never equals the number of
 * steps, so this has to both stretch and compress:
 *
 *  - **Fewer posts than steps**: keep the structurally important ones. Dropping
 *    the announcement to keep a teaser would be absurd, so steps are ranked by
 *    importance and the weakest are dropped first — never by truncating the
 *    list, which would always lose the thank-you.
 *  - **More posts than steps**: duplicate the steps marked repeatable
 *    (reminders, social proof), spreading copies across the gap rather than
 *    stacking them.
 */
export function expandStructure(steps, postCount) {
    if (steps.length === 0 || postCount <= 0)
        return [];
    const ordered = [...steps].sort((a, b) => a.at - b.at);
    let chosen;
    if (postCount >= ordered.length) {
        chosen = [...ordered];
        const repeatable = ordered.filter((s) => s.repeatable);
        if (repeatable.length === 0) {
            // Nothing is safe to repeat, so the campaign is simply shorter than
            // asked for. Saying so beats inventing a second "last chance".
            return toExpanded(chosen);
        }
        let extra = postCount - ordered.length;
        let i = 0;
        while (extra > 0) {
            const base = repeatable[i % repeatable.length];
            chosen.push({ ...base });
            extra--;
            i++;
        }
        // Re-spread the duplicates: without this, three extra reminders all carry
        // the original's `at` and land on the same day.
        chosen = respread(chosen);
    }
    else {
        chosen = keepMostImportant(ordered, postCount);
    }
    return toExpanded(chosen.sort((a, b) => a.at - b.at));
}
/**
 * Importance order when a campaign has to be trimmed.
 *
 * An announcement is the campaign; a teaser without one is a riddle. Lower
 * number = kept longer.
 */
const ROLE_PRIORITY = {
    announcement: 0,
    last_chance: 1,
    reminder: 2,
    teaser: 3,
    thank_you: 4,
    social_proof: 5,
    engagement: 6,
    behind_scenes: 7,
    tip: 8,
};
function keepMostImportant(steps, count) {
    // Stable by original position so two steps of equal priority keep their order.
    const ranked = steps
        .map((step, position) => ({ step, position }))
        .sort((a, b) => ROLE_PRIORITY[a.step.role] - ROLE_PRIORITY[b.step.role] || a.position - b.position);
    return ranked.slice(0, count).map((r) => r.step);
}
/** Evenly redistributes `at` across 0–1, preserving order. */
function respread(steps) {
    const sorted = [...steps].sort((a, b) => a.at - b.at);
    if (sorted.length === 1)
        return sorted;
    return sorted.map((step, i) => ({
        ...step,
        at: i / (sorted.length - 1),
    }));
}
function toExpanded(steps) {
    return steps.map((step, index) => ({
        role: step.role,
        guidance: step.guidance,
        index,
        at: step.at,
    }));
}
/**
 * Picks which of the available days each expanded step falls on.
 *
 * Takes the days the brand's schedule rules already allow (from
 * pickPostingDays) and assigns them by position, so a campaign still respects
 * "no posting on Sundays" while keeping its narrative order.
 *
 * Returns fewer entries than steps when there are not enough allowed days —
 * the caller reports that rather than doubling up, because two campaign posts
 * on one day defeats the pacing the structure exists to create.
 */
export function assignDays(steps, availableDays) {
    if (availableDays.length === 0 || steps.length === 0)
        return [];
    const days = [...availableDays].sort();
    const used = new Set();
    const out = [];
    for (const step of steps) {
        // Map the step's position in the window onto the available days.
        let index = Math.round(step.at * (days.length - 1));
        // Taken already: walk forward, then backward. Forward first so the
        // narrative order is preserved wherever possible.
        let probe = index;
        while (probe < days.length && used.has(probe))
            probe++;
        if (probe >= days.length) {
            probe = index;
            while (probe >= 0 && used.has(probe))
                probe--;
        }
        if (probe < 0 || probe >= days.length)
            break; // no day left
        index = probe;
        used.add(index);
        out.push({ step, day: days[index] });
    }
    return out.sort((a, b) => a.day.localeCompare(b.day));
}
//# sourceMappingURL=structure.js.map